/**
 * Renders ChatGPT-authored templates with canonical fact tokens.
 * Missing optional tokens remove the surrounding clause; never emit "undefined" or NaN.
 */

export type NarrativeTokenMap = Record<string, string | number | null | undefined>;

const TOKEN_RE = /\{([a-zA-Z0-9_]+)\}/g;

/** Fix common template/token join bugs after substitution (trimmed optional clauses, leading commas on trend tokens). */
export function normalizeNarrativePunctuation(text: string): string {
  let out = text;
  while (out.includes(',,')) {
    out = out.replace(/,\s*,/g, ',');
  }
  out = out.replace(/([.!?])([A-Za-zÀ-ÿ])/g, '$1 $2');
  return out;
}

function stringifyToken(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return String(value);
  }
  const s = String(value).trim();
  if (!s || s === 'undefined' || s === 'NaN' || s === 'null') return null;
  return s;
}

/**
 * Replace {tokens}. If a token value is missing, remove optional sentence/clause fragments:
 * - trailing optional blocks like `{estimatedYearsSentence}` become empty
 * - if result contains unfilled `{token}`, strip from that token to end of sentence or whole template segment
 */
export function renderNarrativeTemplate(
  template: string,
  tokens: NarrativeTokenMap
): string {
  if (!template?.trim()) return '';

  let out = template.replace(TOKEN_RE, (_, key: string) => {
    const v = stringifyToken(tokens[key]);
    return v ?? '';
  });

  // Remove empty optional sentences (double spaces, lone punctuation)
  out = out.replace(/\s{2,}/g, ' ');
  out = out.replace(/\s+([.,;:])/g, '$1');
  out = out.replace(/,\s*\./g, '.');
  out = normalizeNarrativePunctuation(out);
  out = out.trim();

  // Drop sentences that became too short / broken after missing facts
  const sentences = out.split(/(?<=[.!?])\s+/).filter((s) => s.length > 12);
  return normalizeNarrativePunctuation(sentences.join(' ').trim());
}

export function renderOptionalSentence(
  template: string | undefined,
  tokens: NarrativeTokenMap
): string {
  if (!template?.trim()) return '';
  const rendered = renderNarrativeTemplate(template, tokens);
  return rendered;
}
